import { type InputHTMLAttributes, type ReactNode, useId } from 'react'
import { cn } from '@/shared/lib'

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  // Подпись справа от коробки. Если задана — компонент сам рендерит <label
  // htmlFor> (клик по тексту переключает), иначе отдаёт голую коробку под aria.
  label?: ReactNode
  labelClassName?: string
}

// Кастомный чекбокс: нативный input под appearance-none + SVG-галочка поверх —
// вместо дефолтной браузерной коробки. Наследует checked/disabled/aria от input.
export function Checkbox({ className, label, labelClassName, id, ...rest }: CheckboxProps) {
  const autoId = useId()
  const inputId = id ?? autoId

  const box = (
    <span className="relative inline-grid size-4 shrink-0 place-items-center">
      <input
        id={inputId}
        type="checkbox"
        className={cn(
          'peer col-start-1 row-start-1 size-4 cursor-pointer appearance-none rounded-[5px]',
          'border border-border-strong bg-bg-sunken transition-colors',
          'hover:border-border-accent',
          'checked:border-accent checked:bg-accent',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      />
      <svg
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className="pointer-events-none col-start-1 row-start-1 size-3 scale-50 text-text-inverse opacity-0 transition peer-checked:scale-100 peer-checked:opacity-100"
      >
        <path
          d="M3.5 8.4l2.9 2.9L12.6 5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )

  if (label == null) return box

  return (
    <label
      htmlFor={inputId}
      className={cn('inline-flex cursor-pointer items-center gap-2', labelClassName)}
    >
      {box}
      {label}
    </label>
  )
}
